import React, { useState } from 'react';
import styled from 'styled-components';
import { useWindows } from '../../contexts/WindowContext';
import TerminalApp from '../Applications/Terminal/TerminalApp';
import ProjectsExplorer from '../Applications/ProjectsExplorer/ProjectsExplorer';
import SystemAppInfo from './SystemAppInfo';
import YouTubeChannel from '../Applications/YouTubeChannel/YouTubeChannel';
import HackerGames from '../Applications/HackerGames/HackerGames';

const IconsContainer = styled.div`
  position: absolute;
  top: 60px;
  left: 20px;
  display: grid;
  grid-template-columns: repeat(2, 90px);
  gap: 25px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 70px);
    gap: 20px;
  }
`;

const DesktopIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  color: #00ff00;
  font-size: 11px;
  width: 90px;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.15);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const IconImage = styled.div`
  width: 56px;
  height: 56px;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid rgba(0, 255, 0, 0.4);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  position: relative;

  ${DesktopIcon}:hover & {
    border-color: #00ff00;
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.4);
    background: rgba(0, 255, 0, 0.1);
  }

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, transparent, rgba(0, 255, 0, 0.2), transparent);
    border-radius: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  ${DesktopIcon}:hover &::after {
    opacity: 1;
  }
`;

const IconLabel = styled.span`
  text-align: center;
  word-wrap: break-word;
  line-height: 1.3;
  font-weight: 500;
`;

const DesktopIcons: React.FC = () => {
  const { openWindow } = useWindows();
  const [selectedApp, setSelectedApp] = useState<string | null>(null);

  const icons = [
    { 
      name: 'Terminal', 
      icon: '💻',
      type: 'app',
      action: () => openWindow({
        title: 'Terminal Emulator',
        component: TerminalApp,
        isMinimized: false,
        isMaximized: false,
        position: { x: 150, y: 150 },
        size: { width: 700, height: 500 }
      })
    },
    { 
      name: 'Projects', 
      icon: '📁',
      type: 'app',
      action: () => openWindow({
        title: 'Projects Explorer',
        component: ProjectsExplorer,
        isMinimized: false,
        isMaximized: false,
        position: { x: 200, y: 100 },
        size: { width: 800, height: 600 }
      })
    },
    { 
      name: 'YouTube', 
      icon: '📺',
      type: 'app',
      action: () => openWindow({
        title: 'CYB3RFY YouTube Channel',
        component: YouTubeChannel,
        isMinimized: false,
        isMaximized: false,
        position: { x: 180, y: 120 },
        size: { width: 900, height: 700 }
      })
    },
    
    { 
      name: 'Games',
      icon: '🎮',
      type: 'app',
      action: () => openWindow({
        title: 'Hacker Games',
        component: HackerGames,
        isMinimized: false,
        isMaximized: false,
        position: { x: 160, y: 140 },
        size: { width: 800, height: 650 }
      })
    },
    { 
      name: 'Burp Suite', 
      icon: '🔍',
      type: 'info',
      action: () => setSelectedApp('Burp Suite')
    },
    { 
      name: 'Metasploit', 
      icon: '🎯',
      type: 'info',
      action: () => setSelectedApp('Metasploit')
    },
    { 
      name: 'Wireshark', 
      icon: '📊',
      type: 'info',
      action: () => setSelectedApp('Wireshark')
    },
    { 
      name: 'Nmap', 
      icon: '🗺️',
      type: 'info',
      action: () => setSelectedApp('Nmap')
    },
    { 
      name: 'John', 
      icon: '🔐',
      type: 'info',
      action: () => setSelectedApp('John')
    },
    { 
      name: 'Hydra', 
      icon: '💧',
      type: 'info',
      action: () => setSelectedApp('Hydra')
    }
  ];

  return (
    <>
      <IconsContainer>
        {icons.map((icon) => (
          <DesktopIcon key={icon.name} onDoubleClick={icon.action}>
            <IconImage>{icon.icon}</IconImage>
            <IconLabel>{icon.name}</IconLabel>
          </DesktopIcon>
        ))}
      </IconsContainer>
      
      <SystemAppInfo 
        appName={selectedApp} 
        onClose={() => setSelectedApp(null)} 
      />
    </>
  );
};

export default DesktopIcons;
